import {Form, Input, InputNumber, Select, Switch, Tooltip} from 'antd';
import styles from '../../collection.module.scss';
import {Button} from '@dabafinance/react-components';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {GET_ALL_COLLECTION_GROUPS} from 'graphql/queries/collections';
import {GET_ALL_STOCKS} from 'graphql/queries/stocks';
import {CollectionConstituents} from './CollectionConstituents';
import {
  collectionCurrencyOptions,
  supportedCurrency,
} from '../../../currencies';

/**
 * Market, constituents and unit rules.
 *
 * The exchange value carries the code alongside the id as `${id}~${code}`,
 * because the write needs the id and the constituent picker needs the code.
 */
export const FinancialInfo = ({
  secondForm,
  handleSecondFormSubmit,
  onTabChange,
  allowUsers,
  setAllowUsers,
  tradeable,
  setTradeable,
  constituents,
  setConstituents,
  selectedExchange,
  setSelectedExchange,
}) => {
  const exchangeCode = selectedExchange?.split('~')[1];

  const {data: stockExchange, loading: stockLoading} = useQuery(
    GET_ALL_STOCKS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error?.message),
    },
  );

  const {data, loading} = useQuery(GET_ALL_COLLECTION_GROUPS, {
    variables: {
      filters: {
        paginate: false,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
  });

  const groups = data?.getAllCollectionGroups?.data?.collectionGroups ?? [];

  const exchanges = stockExchange?.getAllStocksExchange ?? [];

  const onExchangeChange = value => {
    // Constituents are scoped to the exchange, so anything already chosen is
    // guaranteed to be rejected once the exchange changes.
    const configured = constituents?.filter(row => row?.instrumentId) ?? [];
    if (configured.length) {
      toast.info(
        `Cleared ${configured.length} constituent${
          configured.length === 1 ? '' : 's'
        } — they are not listed on the new exchange.`,
      );
      setConstituents([{}]);
    }
    setSelectedExchange(value);

    // A collection trades in the currency its venue trades in, so the exchange
    // answers this question by default. It stays editable because the enum
    // allows a venue to be quoted in something other than its base currency.
    const venue = exchanges.find(item => item?.id === value?.split('~')[0]);
    const base = supportedCurrency(venue?.baseCurrencyCode);
    if (base) {
      secondForm.setFieldsValue({currency: base});
    }
  };

  return (
    <div>
      <Form
        form={secondForm}
        className={styles.form}
        layout="vertical"
        onFinish={handleSecondFormSubmit}>
        <Form.Item
          name="stockExchange"
          label="Exchange"
          className={styles.field}
          rules={[
            {
              required: true,
              message: 'An exchange is required',
            },
          ]}>
          <Select
            className={styles.form}
            loading={stockLoading}
            placeholder="Select an exchange"
            showSearch
            disabled={stockLoading}
            onChange={onExchangeChange}
            optionFilterProp="children">
            {exchanges.map(item => (
              <Select.Option value={`${item?.id}~${item?.code}`} key={item?.id}>
                {item?.code} — {item?.stockExchangeName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="currency"
          label="Currency"
          className={styles.field}
          extra="Defaults to the exchange's base currency. Change it only if this collection is quoted in another."
          rules={[
            {
              required: true,
              message: 'A currency is required',
            },
          ]}>
          <Select
            className={styles.form}
            placeholder="Select an exchange first"
            showSearch
            optionFilterProp="children">
            {collectionCurrencyOptions.map(option => (
              <Select.Option value={option.value} key={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <CollectionConstituents
          exchangeCode={exchangeCode}
          rows={constituents}
          setRows={setConstituents}
        />

        <Form.Item className={styles.field}>
          <div className={styles.switch}>
            <Tooltip title="Off: users can see this collection once it is Active, but cannot buy it.">
              <span>Open for buying</span>
            </Tooltip>
            <Switch checked={tradeable} onChange={setTradeable} />
          </div>
        </Form.Item>

        <Form.Item className={styles.field}>
          <div className={styles.switch}>
            <span>Allow users to remove a constituent from the collection</span>
            <Switch checked={allowUsers} onChange={setAllowUsers} />
          </div>
        </Form.Item>

        {allowUsers && (
          <Form.Item
            name="minInstrumentCount"
            className={styles.field}
            label={
              <Tooltip title="How many constituents must remain after a user removes the ones they do not want.">
                Minimum constituents retained
              </Tooltip>
            }
            rules={[
              {
                required: true,
                message: 'Set how many constituents must remain',
              },
            ]}>
            <InputNumber
              min={1}
              max={
                constituents?.filter(row => row?.instrumentId)?.length ||
                undefined
              }
              className={styles.number}
            />
          </Form.Item>
        )}

        <Form.Item
          name="linkedCollectionGroups"
          className={styles.field}
          label="Linked collection groups">
          <Select
            className={styles.field}
            loading={loading}
            mode="multiple"
            allowClear
            placeholder={
              groups.length ? 'Select groups' : 'No collection groups exist yet'
            }
            disabled={!groups.length}>
            {groups.map(group => (
              <Select.Option key={group?.id} value={group?.id}>
                {group?.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="orderType"
          label="Order type"
          className={styles.field}
          rules={[
            {
              required: true,
              message: 'An order type is required',
            },
          ]}>
          <Select
            className={styles.form}
            placeholder="Select an order type"
            showSearch
            optionFilterProp="children">
            <Select.Option value={'MARKET'}>Market order</Select.Option>
            <Select.Option value={'LIMIT'}>Limit order</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="ticker"
          label="Ticker"
          className={styles.field}
          normalize={value => value?.toUpperCase()?.trim()}
          rules={[
            {
              required: true,
              message: 'A collection ticker is required',
            },
          ]}>
          <Input placeholder="BRVM-AGRI" />
        </Form.Item>

        <div className={styles.buttons}>
          <Button
            label="Previous"
            type="secondary"
            onClick={() => onTabChange('GENERAL')}
          />
          <SubmitButton smallWidth={true} label="Next" />
        </div>
      </Form>
    </div>
  );
};
